/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { CollectionsModule, MODULE_LAYOUTS } from '@coursera-web/cds-labs';

import { useLocation } from 'js/lib/useRouter';

import { Grid, Link, Typography2, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type {
  CarouselCollection,
  CollectionEntity,
  CoreCollectionEntityWithMetadata,
} from 'bundles/collections-carousel/types/CarouselCollection';
import { getCarouselEntityFromProductCard } from 'bundles/collections-carousel/utils/mappingUtils';
import { QUESTION_IDS, type QUESTION_IDS_TYPE } from 'bundles/onboarding-common/constants/questionScheme';
import useBuildOnboardingUrl from 'bundles/onboarding-common/hooks/useBuildOnboardingUrl';
import useComplexCollectionOnFilterData, {
  parseFilterValue,
} from 'bundles/page-config-common/hooks/useComplexCollectionOnFilterData';
import type { ComplexCollectionOnFilterEntityFragment as Entity } from 'bundles/page-config-common/queries/__generated__/GetComplexCollectionOnFilterQuery';
import type {
  PageConfig_CollectionComponentCollectionEntityFragment as Collection,
  PageConfig_FiltersFragment as Filter,
  PageConfig_CollectionComponentCollectionEntityRecommendationFragment as RecommendationCollection,
} from 'bundles/page-config-common/queries/page-config/__generated__/PageConfig_CollectionComponentMetadata';
import { shouldShowFreeQueryPageConfigPreview } from 'bundles/search-common/utils/pageConfigUtils';

import _t from 'i18n!nls/search-common';

type CTA = {
  text: string;
  onboardingQuestionId: QUESTION_IDS_TYPE;
};

type Props = {
  collection: Collection | RecommendationCollection;
  filters: Filter[];
  additionalInput?: Record<string, unknown>;
  cta?: CTA;
};

const styles = {
  container: css`
    margin-top: var(--cds-spacing-100);
    margin-bottom: var(--cds-spacing-100);

    > div {
      /* This is to negate the margin of the CollectionsModule */
      margin-top: calc(var(--cds-spacing-200) * -1);
      margin-bottom: calc(var(--cds-spacing-200) * -1);
      ${breakpoints.down('sm')} {
        margin-top: calc(var(--cds-spacing-100) * -1);
        margin-bottom: calc(var(--cds-spacing-100) * -1);
      }
    }
  `,
  heading: css`
    margin-bottom: var(--cds-spacing-200);
  `,
  headlineContainer: css`
    gap: var(--cds-spacing-200);
    display: flex;
    align-items: end;
  `,
};

const createMockCollection = (): CoreCollectionEntityWithMetadata => {
  return {
    __typename: 'Collection_Collection',
    id: '',
    description: '',
    recommenderConfigurationHash: '',
    context: '',
    collectionTrackingId: '',
    entities: { elements: [] },
    metadata: {},
  };
};

// Helper function to validate if a filter value is valid JSON
const isValidJsonFilter = (filter: Filter): boolean => {
  // Empty values are considered valid (default filter)
  if (!filter.value || filter.value.trim() === '') {
    return true;
  }

  // Use parseFilterValue and check if it returns an object with keys
  const parsed = parseFilterValue(filter.value);
  return Object.keys(parsed).length > 0;
};

const HeadlineWithCTA = ({
  title,
  linkText,
  onboardingQuestionId = QUESTION_IDS.learningGoal,
}: {
  title: string;
  linkText?: string;
  onboardingQuestionId?: QUESTION_IDS_TYPE;
}) => {
  const track = useTracker();
  const { onboardingUrl } = useBuildOnboardingUrl({ questionId: onboardingQuestionId });

  const onClick = () => {
    track('click_button', {
      button: {
        linkURL: onboardingUrl,
        linkName: 'onboarding_edit',
        name: 'onboarding_edit',
      },
    });
  };

  return (
    <div css={styles.heading}>
      <Grid container component="div" direction="row" css={styles.headlineContainer}>
        <Typography2 variant="subtitleLarge" component="h2">
          {title}
        </Typography2>
        {linkText && (
          <Link
            variant="quiet"
            typographyVariant="subtitleMedium"
            href={onboardingUrl}
            onClick={onClick}
            data-testid="edit-goal-link"
          >
            {linkText ?? _t('Edit goal')}
          </Link>
        )}
      </Grid>
    </div>
  );
};

// Note: this complex collection is using the legacy pattern, please contact the recs team for the updated pattern
const ComplexCollectionModule = ({ additionalInput, cta, collection, filters = [] }: Props) => {
  const location = useLocation();
  const urlQuery = location?.query?.query || '';

  // Filter out invalid JSON filters before passing to the hook
  const validFilters = filters.filter(isValidJsonFilter);

  const { entities, loading, activeFilterIndex, handleFilterChange } = useComplexCollectionOnFilterData({
    additionalInput,
    collection,
    filters: validFilters,
  });

  const track = useTracker();

  // Transform valid filters to CDS-Labs component format
  const filterList = validFilters.map((filter, index) => ({
    id: String(index),
    filterName: filter.name,
  }));

  const handleFilterChangeWithEventing = (index: number, filterName: string) => {
    track('click_button', {
      button: {
        name: 'collections_filter_chip',
        linkName: `collections_filter_chip_${filterName.replace(/\s+/g, '_').toLowerCase()}`,
      },
    });
    handleFilterChange(index);
  };

  // Transform entities to match the CarouselCollection type using the library's utility function
  const transformedEntities: CollectionEntity[] = entities
    .filter((entity): entity is Entity => entity !== null)
    .map((entity, index) =>
      // Add required properties to match ProductCard_ProductCard type
      getCarouselEntityFromProductCard(
        {
          id: entity.id || '',
          badges: entity.badges,
          marketingProductType: entity.marketingProductType || '',
          productTypeAttributes: entity.productTypeAttributes,
        },
        index,
        createMockCollection(),
        shouldShowFreeQueryPageConfigPreview(urlQuery)
      )
    )
    .filter(Boolean);

  // Format collections in the structure expected by CollectionsModule
  const collections: CarouselCollection[] = [
    {
      collectionId: collection.id,
      title: collection.description,
      entities: transformedEntities,
    },
  ];

  const customHeadline = (
    <HeadlineWithCTA
      title={collection.description}
      linkText={cta?.text}
      onboardingQuestionId={cta?.onboardingQuestionId}
    />
  );
  return (
    <div css={styles.container}>
      <CollectionsModule
        headline={collection.description}
        collections={collections}
        isCollectionsLoading={loading}
        filters={filterList}
        currentCollectionSetIndex={activeFilterIndex}
        onFilterChange={handleFilterChangeWithEventing}
        layout={MODULE_LAYOUTS.textTopImageBottom}
        customHeadline={customHeadline}
      />
    </div>
  );
};

export default ComplexCollectionModule;
